/**
 * =============================================================================
 * Componente: ReferralReferralDetailSheet
 * Versão: 2.0.0
 *
 * Descrição:
 *   Sheet lateral premium com detalhes completos de uma INDICAÇÃO. Padronizado
 *   1:1 com `CommissionDetailSheet` e `PayoutDetailSheet` (módulo Indicação):
 *
 *   - Sheet à direita, largura `w-[480px]` em desktop e fullscreen no mobile.
 *   - Header com chip de ícone (UserPlus) + nome do lead + UUID curto + badge
 *     de status.
 *   - Action Bar contextual gated por `usePermissions().canEdit('referral_referrals')`:
 *       - Reprocessar integração CRM
 *       - Marcar como perdida (em fases iniciais)
 *   - Blocos: Lead, Origem, Afiliado, CRM, Comissões geradas, Valor de
 *     conversão e Linha do Tempo (auditoria/observabilidade).
 *   - Realtime via `useReferralReferralDetails`.
 *
 * Segurança/UX:
 *   - UUIDs são exibidos truncados (8 chars uppercase) via `shortId()`.
 *   - A RPC entrega UUIDs completos para que a navegação para entidades CRM
 *     (Negócio, Contato, Vendedor, Comissão) funcione corretamente.
 *
 * RELEASE:
 *   2026-04-26 v1.0.0 - Implementação inicial (legado).
 *   2026-04-30 v2.0.0 - Reescrito 1:1 com CommissionDetailSheet:
 *                       header premium com gradient, action bar gated,
 *                       blocos em cards, Comissões geradas clicáveis,
 *                       Timeline cronológica completa de auditoria,
 *                       skeleton/erro padronizados, navegação CRM.
 * =============================================================================
 */
import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  UserPlus,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  AlertCircle,
  AlertTriangle,
  History,
  User as UserIcon,
  Briefcase,
  ExternalLink,
  StickyNote,
  Mail,
  Phone,
  Info,
  Megaphone,
  Tag,
  Target,
  Receipt,
  RotateCcw,
  Loader2,
  CircleDollarSign,
  Link2,
  HandCoins,
  KeyRound,
  Building2,
  Compass,
  Users,
  ChevronDown,
  Activity,
  FolderTree,
} from 'lucide-react';
import { DealStatusBadge } from '@/components/crm/deals/DealStatusBadge';
import { ReferralStatusBadge } from '@/components/referral/ReferralStatusBadge';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useReferralReferralDetails,
  type ReferralTimelineEvent,
  type ReferralTimelineAction,
  type ReferralDetailsCommission,
} from '@/hooks/useReferralReferralDetails';
import type { ReferralAdminStatus } from '@/hooks/useReferralReferralsAdmin';
import type { CommissionStatus } from '@/hooks/useReferralCommissionsPaginated';

// ============================================================================
// PROPS
// ============================================================================

export type ReferralDetailAction = 'reprocess' | 'mark_lost';

interface ReferralReferralDetailSheetProps {
  /** ID da indicação — `null` mantém o Sheet fechado. */
  referralId: string | null;
  /** Callback de fechamento. */
  onClose: () => void;
  /** Reprocessamento manual da integração CRM. */
  onReprocess?: (referralId: string) => void;
  /** Estado de loading do reprocessamento. */
  isReprocessing?: boolean;
  /** Abre o modal premium do negócio (CRM). */
  onOpenDeal?: (dealId: string) => void;
  /** Abre o sheet de detalhes da comissão. */
  onOpenCommission?: (commissionId: string) => void;
}

// ============================================================================
// CONSTANTES DE STATUS
// ============================================================================

const STATUS_CONFIG: Record<
  ReferralAdminStatus,
  { label: string; color: string; dot: string }
> = {
  pending:   { label: 'Pendente',    color: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60', dot: 'bg-amber-500' },
  qualified: { label: 'Qualificada', color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60',       dot: 'bg-blue-500' },
  converted: { label: 'Convertida',  color: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60', dot: 'bg-emerald-500' },
  lost:      { label: 'Perdida',     color: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/60',       dot: 'bg-rose-500' },
};

const COMMISSION_STATUS_CONFIG: Record<CommissionStatus, { label: string; color: string; dot: string }> = {
  pending:   { label: 'Pendente',  color: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300', dot: 'bg-amber-500' },
  approved:  { label: 'Aprovada',  color: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',     dot: 'bg-blue-500' },
  paid:      { label: 'Paga',      color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelada', color: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300',     dot: 'bg-rose-500' },
  reversed:  { label: 'Revertida', color: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
};

// Status do Deal: agora utiliza o componente compartilhado DealStatusBadge
// (mesmo da listagem de Negócios). As constantes locais foram removidas.


// ============================================================================
// HELPERS
// ============================================================================

function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

function formatLong(date: string | null | undefined): string | null {
  if (!date) return null;
  return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

function shortId(id: string | null | undefined): string {
  if (!id) return '';
  return id.substring(0, 8).toUpperCase();
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = String(raw).replace(/\D+/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

// ============================================================================
// SUBCOMPONENTES VISUAIS (espelham CommissionDetailSheet)
// ============================================================================

function SectionLabel({
  icon: Icon,
  children,
  right,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {children}
      </div>
      {right}
    </div>
  );
}

function InfoRow({
  label,
  value,
  icon: Icon,
  className,
}: {
  label: string;
  value: React.ReactNode;
  /**
   * Ícone opcional exibido à esquerda do label.
   * Padroniza o visual conforme o modal de Negócio do CRM.
   */
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 px-3 py-2 text-sm rounded-md',
        className,
      )}
    >
      <span className="text-xs text-muted-foreground shrink-0 self-center inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground/70" />}
        {label}
      </span>
      <span className="text-right text-sm font-medium tabular-nums break-words self-center">
        {value}
      </span>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Action Bar contextual (gated por permissão)
// ============================================================================

function ReferralActionBar({
  status,
  referralId,
  hasError,
  hasContact,
  hasDeal,
  isReprocessing,
  onReprocess,
}: {
  status: ReferralAdminStatus;
  referralId: string;
  hasError: boolean;
  hasContact: boolean;
  hasDeal: boolean;
  isReprocessing?: boolean;
  onReprocess?: (id: string) => void;
}) {
  // Reprocessar só faz sentido quando algo está faltando ou houve erro
  const canReprocess = !!onReprocess && (hasError || !hasContact || !hasDeal);
  if (!canReprocess) return null;

  const baseBtn = 'h-8 px-3 text-xs font-medium gap-1.5 rounded-md';

  return (
    <div className="space-y-2">
      <Button
        onClick={() => onReprocess!(referralId)}
        disabled={isReprocessing}
        className={cn(
          baseBtn,
          'w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
        )}
      >
        {isReprocessing ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Reprocessando…
          </>
        ) : (
          <>
            <RotateCcw className="h-3.5 w-3.5" />
            Reprocessar integração CRM
          </>
        )}
      </Button>
      {hasError && (
        <p className="text-[11px] text-muted-foreground text-center">
          Use para tentar criar novamente o contato/negócio no CRM.
        </p>
      )}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Card de comissão gerada
// ============================================================================

function CommissionCardRow({
  commission,
  onOpenCommission,
}: {
  commission: ReferralDetailsCommission;
  onOpenCommission?: (commissionId: string) => void;
}) {
  const statusInfo = COMMISSION_STATUS_CONFIG[commission.status] ?? COMMISSION_STATUS_CONFIG.pending;
  const clickable = !!onOpenCommission && !!commission.id;

  return (
    <div
      className={cn(
        'group relative flex gap-3 rounded-xl border border-border/60 bg-card p-3',
        'transition-all duration-200 hover:border-border hover:shadow-sm',
        'border-l-[3px] border-l-primary/40 hover:border-l-primary',
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <HandCoins className="h-5 w-5" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Comissão
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (clickable) onOpenCommission!(commission.id);
            }}
            disabled={!clickable}
            className={cn(
              'inline-flex items-center gap-1 font-mono text-xs uppercase min-w-0',
              clickable
                ? 'text-primary hover:underline cursor-pointer'
                : 'text-muted-foreground cursor-default',
            )}
            title={clickable ? 'Abrir comissão' : undefined}
          >
            <span className="truncate">#{shortId(commission.id)}</span>
            {clickable && <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            className={cn('gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium', statusInfo.color)}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', statusInfo.dot)} />
            {statusInfo.label}
          </Badge>
        </div>

        <div className="text-[11px] text-muted-foreground tabular-nums leading-tight space-y-0.5">
          <div>
            Criada em <span className="text-foreground/80">{formatLong(commission.created_at)}</span>
          </div>
          {commission.paid_at && (
            <div>
              Paga em <span className="text-foreground/80">{formatLong(commission.paid_at)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end justify-between gap-1.5">
        <span className="text-base font-bold tabular-nums text-foreground leading-none">
          {formatBRL(commission.reward_value)}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Timeline cronológica
// ============================================================================

const ACTION_VISUAL: Record<
  ReferralTimelineAction,
  {
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    iconBg: string;
    label: string;
  }
> = {
  created:              { icon: Clock,            color: 'text-amber-600 dark:text-amber-400',     iconBg: 'bg-amber-100 dark:bg-amber-950/40',   label: 'Indicação criada' },
  qualified:            { icon: CheckCircle2,     color: 'text-blue-600 dark:text-blue-400',       iconBg: 'bg-blue-100 dark:bg-blue-950/40',     label: 'Lead qualificado' },
  crm_contact_created:  { icon: UserIcon,         color: 'text-indigo-600 dark:text-indigo-400',   iconBg: 'bg-indigo-100 dark:bg-indigo-950/40', label: 'Contato criado no CRM' },
  crm_deal_created:     { icon: Briefcase,        color: 'text-violet-600 dark:text-violet-400',   iconBg: 'bg-violet-100 dark:bg-violet-950/40', label: 'Negócio criado no CRM' },
  crm_processing_error: { icon: AlertTriangle,    color: 'text-amber-600 dark:text-amber-400',     iconBg: 'bg-amber-100 dark:bg-amber-950/40',   label: 'Falha na integração CRM' },
  crm_reprocessed:      { icon: RotateCcw,        color: 'text-cyan-600 dark:text-cyan-400',       iconBg: 'bg-cyan-100 dark:bg-cyan-950/40',     label: 'Integração reprocessada' },
  converted:            { icon: CircleDollarSign, color: 'text-emerald-600 dark:text-emerald-400', iconBg: 'bg-emerald-100 dark:bg-emerald-950/40', label: 'Indicação convertida' },
  commission_generated: { icon: HandCoins,        color: 'text-primary',                            iconBg: 'bg-primary/10',                        label: 'Comissão gerada' },
  lost:                 { icon: XCircle,          color: 'text-rose-600 dark:text-rose-400',       iconBg: 'bg-rose-100 dark:bg-rose-950/40',     label: 'Indicação perdida' },
};

function ReferralTimeline({
  events,
  onOpenDeal,
  onOpenCommission,
}: {
  events: ReferralTimelineEvent[];
  onOpenDeal?: (dealId: string) => void;
  onOpenCommission?: (commissionId: string) => void;
}) {
  // Cronológico ASC: mais antigo → mais novo
  const ordered = React.useMemo(
    () =>
      [...events].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    [events],
  );

  if (ordered.length === 0) {
    return <p className="text-xs text-muted-foreground">Nenhum evento registrado.</p>;
  }

  return (
    <ol className="relative space-y-4 pl-1">
      {ordered.map((ev, idx) => {
        const visual = ACTION_VISUAL[ev.action] ?? ACTION_VISUAL.created;
        const Icon = visual.icon;
        const isLast = idx === ordered.length - 1;

        const performerLabel =
          ev.performer_name?.trim() || ev.performer_email?.trim() || 'Sistema';

        const refClickable =
          (ev.reference_type === 'deal' && !!ev.reference_id && !!onOpenDeal) ||
          (ev.reference_type === 'commission' && !!ev.reference_id && !!onOpenCommission);

        const handleRefClick = () => {
          if (!ev.reference_id) return;
          if (ev.reference_type === 'deal' && onOpenDeal) onOpenDeal(ev.reference_id);
          if (ev.reference_type === 'commission' && onOpenCommission) onOpenCommission(ev.reference_id);
        };

        const refLabel =
          ev.reference_type === 'deal'
            ? 'Negócio'
            : ev.reference_type === 'commission'
              ? 'Comissão'
              : ev.reference_type === 'contact'
                ? 'Contato'
                : null;

        return (
          <li key={ev.id} className="relative flex gap-3">
            {!isLast && (
              <span
                aria-hidden
                className="absolute left-[13px] top-7 bottom-[-12px] w-px bg-border/60"
              />
            )}
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-4 ring-background',
                visual.iconBg,
              )}
            >
              <Icon className={cn('h-3.5 w-3.5', visual.color)} />
            </span>

            <div className="flex-1 min-w-0 pt-0.5">
              <p className="text-sm font-semibold leading-tight">{visual.label}</p>

              {(ev.performer_name || ev.performer_email) && (
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed flex items-center gap-1 flex-wrap">
                  <UserIcon className="h-3 w-3 shrink-0" />
                  <span>por</span>
                  <span className="font-medium text-foreground">{performerLabel}</span>
                  {ev.performer_email && ev.performer_name && (
                    <span className="text-muted-foreground/70">({ev.performer_email})</span>
                  )}
                </p>
              )}

              {ev.reference_id && refLabel && (
                <p className="text-xs mt-1">
                  {refLabel}:{' '}
                  {refClickable ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefClick();
                      }}
                      className="font-mono text-primary hover:underline"
                    >
                      #{shortId(ev.reference_id)}
                    </button>
                  ) : (
                    <span className="font-mono text-muted-foreground">
                      #{shortId(ev.reference_id)}
                    </span>
                  )}
                </p>
              )}

              {(ev.reason || ev.notes) && (
                <p className="text-xs text-muted-foreground mt-1 break-words leading-relaxed">
                  {ev.reason && (
                    <>
                      <span className="font-medium text-foreground/80">Motivo:</span> {ev.reason}
                    </>
                  )}
                  {ev.reason && ev.notes && <br />}
                  {ev.notes}
                </p>
              )}

              <p className="text-[11px] text-muted-foreground/70 mt-1 tabular-nums">
                {formatLong(ev.created_at)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL — ReferralReferralDetailSheet
// ============================================================================

export function ReferralReferralDetailSheet({
  referralId,
  onClose,
  onReprocess,
  isReprocessing,
  onOpenDeal,
  onOpenCommission,
}: ReferralReferralDetailSheetProps) {
  const open = !!referralId;
  const { data, isLoading, error } = useReferralReferralDetails(referralId, open);

  const { canEdit } = usePermissions();
  const canEditReferrals = canEdit('referral_referrals');

  const referral = data?.referral;
  const commissions = data?.commissions ?? [];
  const timeline = data?.timeline ?? [];

  const statusInfo = referral ? STATUS_CONFIG[referral.status] ?? STATUS_CONFIG.pending : null;
  const hasError = !!referral?.metadata && (referral.metadata as any).crm_processing_error;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        className="!w-full sm:!w-[480px] sm:!max-w-[480px] p-0 overflow-x-hidden bg-background"
        aria-describedby={undefined}
      >
        {isLoading || (!referral && !error) ? (
          <div className="p-6 space-y-4">
            <SheetHeader className="text-left">
              <SheetTitle className="text-base font-semibold">
                Carregando detalhes da indicação…
              </SheetTitle>
            </SheetHeader>
            <Skeleton className="h-9 w-9 rounded-xl" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : error || !referral ? (
          <div className="p-6 space-y-4">
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center gap-2 text-base font-semibold">
                <AlertCircle className="h-5 w-5 text-destructive" />
                Não foi possível carregar a indicação
              </SheetTitle>
            </SheetHeader>
            <p className="text-sm text-muted-foreground">
              {(error as Error | null)?.message
                ?? 'Ocorreu um erro inesperado ao buscar os detalhes desta indicação. Tente novamente em instantes.'}
            </p>
            <Button variant="outline" size="sm" onClick={onClose}>
              Fechar
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-full">
            {/* ============== Header com gradient sutil ============== */}
            <div className="relative bg-gradient-to-b from-muted/50 via-muted/15 to-transparent px-5 sm:px-6 pt-6 pb-5">
              <SheetHeader className="space-y-3 text-left">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
                    <UserPlus className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-base font-semibold leading-tight truncate">
                      {referral.lead_name ?? 'Indicação'}
                    </SheetTitle>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[11px] text-muted-foreground font-mono uppercase">
                        #{shortId(referral.id)}
                      </span>
                      {statusInfo && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          {/* Status no formato unificado: bolinha colorida + texto */}
                          <ReferralStatusBadge status={referral.status} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>
            </div>

            {/* ============== Corpo ============== */}
            <div className="px-5 sm:px-6 pb-8 space-y-7">
              {/* Action Bar (gated por permissão) */}
              {canEditReferrals && (
                <ReferralActionBar
                  status={referral.status}
                  referralId={referral.id}
                  hasError={!!hasError}
                  hasContact={!!referral.contact_id}
                  hasDeal={!!referral.deal_id}
                  isReprocessing={isReprocessing}
                  onReprocess={onReprocess}
                />
              )}

              {/* Banner de erro de integração */}
              {hasError && (
                <div className="rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 p-3 flex gap-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed">
                    <p className="font-semibold text-amber-900 dark:text-amber-200">
                      Erro no processamento CRM
                    </p>
                    <p className="text-amber-800 dark:text-amber-300 mt-1 break-words">
                      {String((referral.metadata as any).crm_processing_error)}
                    </p>
                  </div>
                </div>
              )}

              {/* ============== Bloco: Lead ============== */}
              <section className="space-y-3">
                <SectionLabel icon={UserIcon}>Lead</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                  <InfoRow
                    label="Nome"
                    value={
                      <span className="text-sm text-foreground break-words">
                        {referral.lead_name || '—'}
                      </span>
                    }
                  />
                  <InfoRow
                    label="E-mail"
                    value={
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        {referral.lead_email || '—'}
                      </span>
                    }
                    className="bg-muted/20"
                  />
                  <InfoRow
                    label="Telefone"
                    value={
                      <span className="inline-flex items-center gap-1.5 text-sm font-mono">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        {formatPhone(referral.lead_phone)}
                      </span>
                    }
                  />
                  {referral.lead_source && (
                    <InfoRow
                      label="Origem"
                      value={referral.lead_source}
                      className="bg-muted/20"
                    />
                  )}
                </div>

                {referral.lead_message && (
                  <div className="rounded-xl border border-border/60 bg-card p-3 flex gap-2.5">
                    <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-xs whitespace-pre-wrap break-words leading-relaxed">
                      {referral.lead_message}
                    </p>
                  </div>
                )}
              </section>

              {/* ============== Bloco: Origem da Indicação ============== */}
              <section className="space-y-3">
                <SectionLabel icon={Target}>Origem da Indicação</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                  {referral.program_name && (
                    <InfoRow
                      label="Programa"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          <Megaphone className="h-3 w-3 text-muted-foreground" />
                          {referral.program_name}
                        </span>
                      }
                    />
                  )}
                  {referral.campaign_name && (
                    <InfoRow
                      label="Campanha"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          <Tag className="h-3 w-3 text-muted-foreground" />
                          {referral.campaign_name}
                        </span>
                      }
                      className={cn(referral.program_name && 'bg-muted/20')}
                    />
                  )}
                </div>
              </section>

              {/* ============== Bloco: Afiliado ============== */}
              {(referral.affiliate_name || referral.affiliate_email) && (
                <section className="space-y-3">
                  <SectionLabel icon={UserIcon}>Afiliado</SectionLabel>
                  <div className="rounded-xl border border-border/60 bg-card p-3 flex items-start gap-3">
                    <SmartAvatar
                      src={referral.affiliate_avatar_url}
                      name={referral.affiliate_name || referral.affiliate_email || '?'}
                      size="md"
                      use3DFallback
                    />
                    <div className="min-w-0 flex-1">
                      {/* Linha 1: Nome (esquerda) + ID curto (direita) — espelha CommissionDetailSheet */}
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold truncate">
                          {referral.affiliate_name || '—'}
                        </p>
                        {referral.affiliate_id && (
                          <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1.5 shrink-0">
                            <KeyRound className="h-3 w-3" />
                            <span>ID:</span>
                            <span className="font-mono uppercase">{shortId(referral.affiliate_id)}</span>
                          </p>
                        )}
                      </div>
                      {referral.affiliate_email && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {referral.affiliate_email}
                        </p>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* ============== Bloco: Contato / Cliente ==============
                  Lógica:
                  - Se houver cliente vinculado (deal_customer_id), exibe primariamente o
                    Cliente e disponibiliza um <details> colapsável com o Contato original.
                  - Caso contrário, exibe apenas o Contato (indicação ainda não virou cliente).
              */}
              {(referral.contact_id || referral.deal_customer_id) && (
                <section className="space-y-3">
                  <SectionLabel
                    icon={referral.deal_customer_id ? Users : UserIcon}
                  >
                    {referral.deal_customer_id ? 'Cliente' : 'Contato'}
                  </SectionLabel>

                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    {referral.deal_customer_id ? (
                      <>
                        {/* Cliente em destaque */}
                        <InfoRow
                          label="Nome"
                          value={
                            <span className="text-sm text-foreground break-words">
                              {referral.deal_customer_name || '—'}
                            </span>
                          }
                        />
                        {/*
                         * v1.0.4 — Substitui "ID do cliente" por CPF/CNPJ.
                         * - Prioriza CNPJ; senão CPF; senão "—".
                         * - Aplica máscara conforme o documento presente.
                         */}
                        <InfoRow
                          label="CPF/CNPJ"
                          value={
                            <span className="inline-flex items-center gap-1 font-mono text-xs text-foreground">
                              <Receipt className="h-3 w-3 shrink-0 text-muted-foreground" />
                              <span className="truncate">
                                {(() => {
                                  // Helper local: aplica máscara apenas em dígitos
                                  const onlyDigits = (s: string) => s.replace(/\D/g, '');
                                  const cnpj = referral.deal_customer_cnpj
                                    ? onlyDigits(referral.deal_customer_cnpj)
                                    : '';
                                  const cpf = referral.deal_customer_cpf
                                    ? onlyDigits(referral.deal_customer_cpf)
                                    : '';
                                  if (cnpj.length === 14) {
                                    return cnpj.replace(
                                      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                                      '$1.$2.$3/$4-$5'
                                    );
                                  }
                                  if (cpf.length === 11) {
                                    return cpf.replace(
                                      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
                                      '$1.$2.$3-$4'
                                    );
                                  }
                                  // Fallback: retorna o valor cru (caso já formatado) ou "—"
                                  return (
                                    referral.deal_customer_cnpj ||
                                    referral.deal_customer_cpf ||
                                    '—'
                                  );
                                })()}
                              </span>
                            </span>
                          }
                          className="bg-muted/20"
                        />


                        {/* Colapsável: contato de origem (existe sempre que houve indicação) */}
                        {referral.contact_id && (
                          <details className="group border-t border-border/60">
                            <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2 text-xs text-muted-foreground hover:bg-muted/30 transition-colors">
                              <span className="inline-flex items-center gap-1.5">
                                <UserIcon className="h-3 w-3" />
                                Ver contato de origem
                              </span>
                              <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                            </summary>
                            <div>
                              <InfoRow
                                label="Nome do contato"
                                value={
                                  <span className="text-sm text-foreground break-words">
                                    {referral.contact_name || '—'}
                                  </span>
                                }
                              />
                              <InfoRow
                                label="ID do contato"
                                value={
                                  <span className="inline-flex items-center gap-1 font-mono text-xs uppercase text-muted-foreground">
                                    <UserIcon className="h-3 w-3 shrink-0" />
                                    <span className="truncate">#{shortId(referral.contact_id)}</span>
                                  </span>
                                }
                                className="bg-muted/20"
                              />
                            </div>
                          </details>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Apenas contato (indicação ainda não virou cliente) */}
                        <InfoRow
                          label="Nome"
                          value={
                            <span className="text-sm text-foreground break-words">
                              {referral.contact_name || '—'}
                            </span>
                          }
                        />
                        <InfoRow
                          label="ID do contato"
                          value={
                            <span className="inline-flex items-center gap-1 font-mono text-xs uppercase text-muted-foreground">
                              <UserIcon className="h-3 w-3 shrink-0" />
                              <span className="truncate">#{shortId(referral.contact_id)}</span>
                            </span>
                          }
                          className="bg-muted/20"
                        />
                      </>
                    )}
                  </div>
                </section>
              )}

              {/* ============== Bloco: Integração CRM ==============
                  Ordem: Status (badge) -> Negócio -> Título -> Valor ->
                         Workspace -> Origem -> Categoria -> Fase -> Vendedor.
                  Contato saiu daqui (agora vive em seção própria acima).
              */}
              {referral.deal_id && (
                <section className="space-y-3">
                  <SectionLabel icon={Briefcase}>Integração CRM</SectionLabel>
                  <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                    {/*
                     * Status do negócio — reutiliza DealStatusBadge (mesmo
                     * componente da listagem de Negócios) para garantir
                     * paridade visual.
                     */}
                    {referral.deal_status && (
                      <InfoRow
                        icon={Activity}
                        label="Status do negócio"
                        value={<DealStatusBadge status={referral.deal_status} />}
                      />
                    )}

                    {/* Negócio (ID clicável) — sem ícone no valor */}
                    <InfoRow
                      icon={Briefcase}
                      label="Negócio"
                      value={
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenDeal && referral.deal_id) onOpenDeal(referral.deal_id);
                          }}
                          disabled={!onOpenDeal}
                          className={cn(
                            'inline-flex items-center gap-1 font-mono text-xs uppercase max-w-full',
                            onOpenDeal
                              ? 'text-primary hover:underline cursor-pointer'
                              : 'text-muted-foreground cursor-default',
                          )}
                          title={referral.deal_title || undefined}
                        >
                          <span className="truncate">#{shortId(referral.deal_id)}</span>
                          {onOpenDeal && <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" />}
                        </button>
                      }
                      className={cn(referral.deal_status && 'bg-muted/20')}
                    />

                    {referral.deal_title && (
                      <InfoRow
                        icon={Tag}
                        label="Título"
                        value={
                          <span className="text-sm text-foreground break-words">
                            {referral.deal_title}
                          </span>
                        }
                      />
                    )}
                    {referral.deal_value != null && (
                      <InfoRow
                        icon={CircleDollarSign}
                        label="Valor do negócio"
                        value={formatBRL(referral.deal_value)}
                        className="bg-muted/20"
                      />
                    )}

                    {/* Workspace — ícone migrado para o label */}
                    {referral.deal_workspace_name && (
                      <InfoRow
                        icon={Building2}
                        label="Workspace"
                        value={referral.deal_workspace_name}
                      />
                    )}

                    {/* Origem do negócio — ícone migrado para o label */}
                    {referral.deal_source && (
                      <InfoRow
                        icon={Compass}
                        label="Origem do negócio"
                        value={referral.deal_source}
                        className={cn(referral.deal_workspace_name && 'bg-muted/20')}
                      />
                    )}

                    {referral.deal_category_name && (
                      <InfoRow
                        icon={FolderTree}
                        label="Categoria"
                        value={referral.deal_category_name}
                      />
                    )}
                    {referral.deal_phase_name && (
                      <InfoRow
                        icon={Target}
                        label="Fase atual"
                        value={
                          <div className="min-w-[140px]">
                            <DealPhaseProgress
                              phaseName={referral.deal_phase_name}
                              phaseColor={referral.deal_phase_color}
                              phaseSortOrder={referral.deal_phase_sort_order ?? 1}
                              totalPhases={referral.deal_pipeline_total_phases ?? 1}
                            />
                          </div>
                        }
                        className="bg-muted/20"
                      />
                    )}
                    {referral.deal_seller_name && (
                      <InfoRow
                        icon={UserIcon}
                        label="Vendedor"
                        value={
                          <span className="inline-flex items-center gap-2">
                            <SmartAvatar
                              src={referral.deal_seller_avatar_url}
                              name={referral.deal_seller_name}
                              size="xs"
                              use3DFallback
                            />
                            <span className="truncate max-w-[160px]">{referral.deal_seller_name}</span>
                          </span>
                        }
                      />
                    )}
                  </div>
                </section>
              )}

              {/* ============== Bloco: Comissões geradas ============== */}
              {commissions.length > 0 && (
                <section className="space-y-3">
                  <SectionLabel icon={HandCoins}>
                    Comissões geradas ({commissions.length})
                  </SectionLabel>
                  <div className="space-y-2">
                    {commissions.map((c) => (
                      <CommissionCardRow
                        key={c.id}
                        commission={c}
                        onOpenCommission={onOpenCommission}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* ============== Bloco: Valor de Conversão ==============
                   Visual sóbrio inspirado em demonstrativos financeiros:
                   - Sem cor "alerta" (emerald saturado);
                   - Layout horizontal: rótulo discreto à esquerda + valor à direita;
                   - Hairline divisor BRL/inteiro/centavos para dar peso editorial.
              */}
              {referral.conversion_value !== null && referral.conversion_value !== undefined && (() => {
                const total = Number(referral.conversion_value ?? 0);
                const [intPart, decPart] = total
                  .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  .split(',');
                return (
                  <section className="space-y-2">
                    <div className="rounded-lg border border-border bg-card">
                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <CircleDollarSign className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 leading-none">
                              Valor de conversão
                            </p>
                            <p className="text-[10px] text-muted-foreground/60 mt-1 leading-none">
                              Receita atribuída a esta indicação
                            </p>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-1 tabular-nums">
                          <span className="text-[11px] font-medium text-muted-foreground/70 mr-0.5">
                            BRL
                          </span>
                          <span className="text-xl font-semibold text-foreground tracking-tight">
                            {intPart}
                          </span>
                          <span className="text-sm font-medium text-muted-foreground">
                            ,{decPart}
                          </span>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              })()}


              {/* ============== Motivo de perda em destaque ============== */}
              {referral.lost_reason && referral.status === 'lost' && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 flex gap-2.5">
                  <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed">
                    <p className="font-semibold text-destructive">Motivo da perda</p>
                    <p className="text-muted-foreground mt-1 break-words">
                      {referral.lost_reason}
                    </p>
                  </div>
                </div>
              )}

              {/* ============== Bloco: Timeline (auditoria) ============== */}
              <section className="space-y-3">
                <SectionLabel icon={History}>Linha do Tempo · Auditoria</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card p-4">
                  <ReferralTimeline
                    events={timeline}
                    onOpenDeal={onOpenDeal}
                    onOpenCommission={onOpenCommission}
                  />
                </div>
              </section>
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}
